import React, { memo, useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { useHistory } from 'react-router-dom';

import { CONTEXT } from '../../config';
import { logging, logPostInfo } from '../../actions/log';
import { getUrlByTargetContent } from '../../utils/utils';
import styles from './WpReco.module.less';
import WallpaperCard from '../../components/WallpaperCard/WallpaperCard';
import { useInView } from 'react-intersection-observer';

const selectLogInfo = createSelector(
  state => state.log,
  log => log.logInfo,
);

const series = {
  '8': {
    small: [1, 6],
    small_space: [2, 3, 7, 0],
    big: [4],
    big_space: [5],
    pageSize: row => {
      return parseInt(row / 3) * 8 + (row % 3 === 1 ? 3 : row % 3 === 2 ? 5 : 0);
    },
  },
  '10': {
    small: [1, 7],
    small_space: [2, 3, 4, 8, 9, 0],
    big: [5],
    big_space: [6],
    pageSize: row => {
      return parseInt(row / 3) * 10 + (row % 3 === 1 ? 4 : row % 3 === 2 ? 6 : 0);
    },
  },
  '11': {
    small: [1, 8],
    small_space: [2, 3, 4, 9, 10, 0],
    big: [5],
    big_space: [6, 7],
    pageSize: row => {
      return parseInt(row / 3) * 11 + (row % 3 === 1 ? 4 : row % 3 === 2 ? 7 : 0);
    },
  },
  '13': {
    small: [1, 9],
    small_space: [2, 3, 4, 5, 10, 11, 12, 0],
    big: [6],
    big_space: [7, 8],
    pageSize: row => {
      return parseInt(row / 3) * 13 + (row % 3 === 1 ? 5 : row % 3 === 2 ? 8 : 0);
    },
  },
  '16': {
    small: [1, 11],
    small_space: [2, 3, 4, 5, 6, 12, 13, 14, 15, 0],
    big: [7],
    big_space: [8, 9, 10],
    pageSize: row => {
      return parseInt(row / 3) * 16 + (row % 3 === 1 ? 6 : row % 3 === 2 ? 10 : 0);
    },
  },
};
export default memo(({ data, logInfo }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const logInfoData = useSelector(selectLogInfo);
  const [showWallpapers, setShowWallpapers] = useState([]);
  const [showRank, setShowRank] = useState({});
  const [showNumber, setShowNumber] = useState(8);

  const LOG_ACTION_SOURCE = {
    action_more: `C_${logInfo?.clTag ?? 'Wallpaper'}_Group_More`,
    source: `${logInfo.tag}_Group`,
  };

  const [ref, inView] = useInView({
    threshold: 1,
  });

  const [refDom, inViewDom] = useInView({
    threshold: 0,
    initialInView: true,
    rootMargin: '600px 0px 600px 0px',
    root: data?.node,
  });

  useEffect(() => {
    if (inView && logInfo && logInfo.changView) {
      logInfo.changView({
        pageCardContentId:
          data && data.dataList && data.dataList[0] && data.dataList[0].pageCardContentId,
        pageCardTitle: data && data.title,
        rankCode: data && data.dataList && data.dataList[0] && data.dataList[0].code,
        cardType: data && data.cardType,
      });
    }
  }, [inView]);

  // 筛洗数据，去除运营配置数据错误情况
  useEffect(() => {
    if (
      data &&
      data.dataList &&
      data.dataList.length > 0 &&
      data.dataList[0].wallpaperList &&
      data.dataList[0].wallpaperList.length > 0
    ) {
      // 3 2 3的展示规则，计算显示的壁纸张数
      const row = parseInt(data.row) || 1;
      // let pageSize = parseInt(row / 3) * 8 + (row % 3 === 1 ? 3 : row % 3 === 2 ? 5 : 0);
      let pageSize = series[showNumber].pageSize(row);
      if (pageSize > data.dataList[0].wallpaperList.length) {
        pageSize = series[showNumber].pageSize(1);
      }
      if (data.dataList[0].wallpaperList.length >= 3) {
        setShowWallpapers(data.dataList[0].wallpaperList.slice(0, pageSize));
        setShowRank(data.dataList[0]);
      }
    }
  }, [dispatch, data, showNumber]);

  // 监听浏览器大小变化自动适应
  useEffect(() => {
    window.addEventListener('resize', handleResize, false);
    handleResize();
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleResize = useCallback(e => {
    const clientWidth = (document.body && document.body.clientWidth) || 1060;
    if (clientWidth < 1330) {
      setShowNumber(8);
    } else if (clientWidth >= 1330 && clientWidth < 1465) {
      setShowNumber(10);
    } else if (clientWidth >= 1465 && clientWidth < 1600) {
      setShowNumber(11);
    } else if (clientWidth >= 1600 && clientWidth < 1870) {
      setShowNumber(13);
    } else if (clientWidth >= 1870) {
      setShowNumber(16);
    }
  }, []);

  const getClassType = useCallback(
    index => {
      const pos = (index + 1) % parseInt(showNumber);
      if (series[showNumber].small.indexOf(pos) > -1) {
        return 'small';
      }
      if (series[showNumber].small_space.indexOf(pos) > -1) {
        return 'small_space';
      }
      if (series[showNumber].big.indexOf(pos) > -1) {
        return 'big';
      }
      if (series[showNumber].big_space.indexOf(pos) > -1) {
        return 'big_space';
      }
    },
    [showNumber],
  );

  const handleShowMore = useCallback(() => {
    // 更新页面 PV
    dispatch(
      logging({
        category: 'Wallpaper',
        action: LOG_ACTION_SOURCE.action_more,
        label: '',
        param2: {
          source: `${LOG_ACTION_SOURCE.source}||${showRank.pageCardContentId}`,
          source_p: logInfoData.source_p,
          source_pp: logInfoData.source_pp,
          code: showRank.code,
          title: data.title,
        },
      }),
    );

    if (data.targetType === '1' || data.targetType === '3' || data.targetType === '11') {
      dispatch(
        logPostInfo({
          source_p: `${LOG_ACTION_SOURCE.source}||${showRank.pageCardContentId}`,
          source_pp: logInfoData.source_p,
        }),
      );
      if (data.targetType && data.targetContent) {
        const url = getUrlByTargetContent(data);
        history.push(url);
      } else {
        history.push(`${CONTEXT}/wallpaper/wpcategory`);
      }
    } else if (data.targetType === '2' && data.targetUrl) {
      window.open(data.targetUrl);
    }
  }, [showRank]);

  return showWallpapers && showWallpapers.length > 0 ? (
    <div
      className={` ${styles.card_box} ${inViewDom ? styles.card_cardIn : styles.card_cardOut} `}
      ref={ref}
    >
      <div className={styles.card} ref={refDom}>
        {
          <>
            <div className={styles.card_header}>
              <div className={styles.card_header_title}>{data.title}</div>
              {data.operate === '2' && data.targetType && data.targetContent && (
                <div
                  className={styles.card_header_opt}
                  onClick={() => {
                    handleShowMore();
                  }}
                >
                  查看更多
                </div>
              )}
            </div>
            <div className={styles.card_wplist}>
              {showWallpapers.map((item, index) => {
                return (
                  <WallpaperCard
                    wallpaper={item}
                    key={index}
                    cardInfo={{
                      pageSource: 'rankCard',
                      code: data.dataList[0].code || '1',
                      classType: getClassType(index),
                      isCollect: true,
                      isWallpaper: true,
                      isDeleteColl: false,
                      isDeleteUsed: false,
                      isPreview: true,
                    }}
                    logInfo={{
                      source: `${LOG_ACTION_SOURCE.source}||${showRank.pageCardContentId}`,
                    }}
                  />
                );
              })}
            </div>
          </>
        }
      </div>
    </div>
  ) : (
    ''
  );
});
